import { useSelector } from 'react-redux';
import { NextPage } from 'next';

import { tariffs } from '@r1-frontend/api-domru/api-content/v1/cities/tariffs';
import {
    TResponseDto as TTariffsResponseDto,
} from '@r1-frontend/api-domru/api-content/v1/cities/tariffs/dto/ResponseDto';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { getBanners } from '~/src/api/content/banners';
import { getPageView } from '~/src/api/content/page/view';
import { ITariff } from '~/src/entities/formattedTariff';
import Layout from '~/src/features/_layout';
import { SeoScriptAggregateRating, SeoScriptAggregateRatingWithOffer } from '~/src/features/_layout/SeoScripts';
import { ISeoScriptAggregateRating } from '~/src/features/_layout/SeoScripts/SeoScriptAggregateRating';
import { EPrivateHouse, InternetRepresent } from '~/src/features/internet/internet-represent';
import { IRepresentTopProps } from '~/src/features/internet/internet-represent/page-blocks/AddOpportunities';
import { initialProps } from '~/src/helpers/basePageFunc';
import { getCookie } from '~/src/helpers/cookie';
import getRequester from '~/src/helpers/getRequester';
import { saveTariffs } from '~/src/store/actions/bundles';
import { setBannerForInternet, setTariffsForInternet } from '~/src/store/actions/internet';
import { getCurrentCity, selectCompanyName } from '~/src/store/selectors/city';
import { selectMinCostedTariff } from '~/src/store/selectors/filteredBundles';
import selectProviderId from '~/src/store/selectors/selectProviderId';

const ADDITIONAL_OPPORTUNITIES_ID = '2911';

type TInternetPageProps = {
    currentCity: ICurrentCity,
    addOpportunities: IRepresentTopProps['addOpportunities'] | null,
    companyName: string,
}

const InternetPage: NextPage<TInternetPageProps> = ({
    addOpportunities,
    currentCity,
    companyName,
}): JSX.Element => {
    const cityText = currentCity.cityIn || '';
    const title = `Подключить интернет на даче от ${companyName} в ${cityText}`;
    const description = `Провести высокоскоростной интернет на дачу от провайдера ${companyName} в ${cityText} по доступной цене.` +
        `Скорость от 100 Мбит/с. Подробнее на сайте или по телефону: ${currentCity.phone}`;
    const minCostedTariff = useSelector(selectMinCostedTariff(
        (tariff: ITariff) => tariff.availableOnInternetPage && tariff.isCottage,
    ));

    const baseSeoData: ISeoScriptAggregateRating = {
        name: 'Интернет на дачу',
        ratingCount: '995',
        ratingValue: '4.7',
    };

    return (
        <>
            {minCostedTariff
                ? <SeoScriptAggregateRatingWithOffer
                    {...minCostedTariff}
                    {...baseSeoData}
                />
                : <SeoScriptAggregateRating {...baseSeoData}/>
            }
            <Layout
                title={title}
                description={description}
                withMenu={true}
                withChat={true}
                withFooter={true}
                bgColor={COLORS.BgMain}
            >
                <InternetRepresent
                    currentCity={currentCity}
                    addOpportunities={addOpportunities}
                    privateHouse={EPrivateHouse.CountryHouse}
                />
            </Layout>
        </>
    );
};

InternetPage.getInitialProps = async(options) => {
    const { validatedAccessToken } = await initialProps(options, { isNeedAuthData: true });

    const { store, req } = options;

    const state = store.getState();
    const {
        internet: {
            tariffs: internetTariffs,
        },
        bundles: {
            tariffs: { allIds: bundlesTariffs },
        },
    } = state;

    const providerId = Number(getCookie('providerId', req) || selectProviderId(state));

    const currentCity = getCurrentCity(state);
    const companyName = selectCompanyName(state);

    const hasTariffs = !!internetTariffs?.length && !!bundlesTariffs?.length;

    const doRequest = getRequester(req);

    const [respAddOpportunities, bannersResponse, tariffsResponse] = await Promise.all([
        getPageView(providerId, ADDITIONAL_OPPORTUNITIES_ID),
        getBanners({ providerId, isAuth: !!validatedAccessToken, tag: 'internet' }),
        hasTariffs
            ? null
            : doRequest.apiContent<TTariffsResponseDto>({
                ...tariffs({ providerId }),
                withProvider: false,
            }),
    ]);

    if (bannersResponse.isSuccess) {
        store.dispatch(setBannerForInternet(bannersResponse.payload[0]?.bannerInfo));
    }

    if (tariffsResponse?.isSuccess) {
        store.dispatch(setTariffsForInternet(tariffsResponse.payload.mono));
        store.dispatch(saveTariffs(tariffsResponse.payload.bundles));
    }

    const addOpportunities = respAddOpportunities.isSuccess ? respAddOpportunities.payload : null;

    return {
        addOpportunities,
        companyName,
        currentCity,
    };
};

export default InternetPage;
